<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <!-- Header -->
        <div class="mb-4">
          <v-btn text @click="volver">
            <v-icon left>mdi-arrow-left</v-icon>
            Volver
          </v-btn>
        </div>

        <!-- Información del Certificado -->
        <v-alert v-if="certificadoInfo" type="info" colored-border border="left" elevation="2" class="mb-4">
          <div class="d-flex align-center">
            <v-icon left size="32" class="mr-3">mdi-file-document-outline</v-icon>
            <div>
              <div class="text-h6 mb-1">Tipo de Certificado</div>
              <v-chip :color="getTipoLeyColor(certificadoInfo.tipo)" small class="mr-2">
                {{ getTipoLeyTexto(certificadoInfo.tipo) }}
              </v-chip>
              <div class="text-caption mt-2">{{ getTipoLeyDescripcion(certificadoInfo.tipo) }}</div>
            </div>
          </div>
        </v-alert>

        <!-- Búsqueda de RUT -->
        <v-card>
          <v-card-title>
            <v-icon left class="mr-2">mdi-account-search</v-icon>
            Búsqueda de Datos del Funcionario
          </v-card-title>
          <v-card-text>
            <!-- Información sobre la búsqueda -->
            <v-alert type="info" outlined border="left" class="mb-4">
              <div class="font-weight-bold mb-2">ℹ️ Información del Registro Civil</div>
              <div class="text-body-2">
                Los datos se obtienen directamente desde el Servicio de Registro Civil e Identificación (SRCeI).
                La consulta incluye: nombre completo, fecha de nacimiento, sexo y estado vital.
              </div>
            </v-alert>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="rut"
                  label="RUT del Funcionario *"
                  outlined
                  placeholder="12345678-9"
                  :rules="[
                    v => !!v || 'El RUT es requerido',
                    rules.rut,
                  ]"
                  :disabled="datosObtenidos"
                  prepend-inner-icon="mdi-card-account-details"
                  @keyup.enter="buscarRut"
                  @input="formatearRut"
                />
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <v-btn
                  color="primary"
                  :loading="buscandoRut"
                  :disabled="!rut || datosObtenidos"
                  @click="buscarRut"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Buscar en Registro Civil
                </v-btn>
                <v-btn
                  v-if="datosObtenidos"
                  text
                  color="warning"
                  class="ml-2"
                  @click="resetearBusqueda"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Buscar otro RUT
                </v-btn>
              </v-col>
            </v-row>

            <!-- Datos obtenidos del Registro Civil -->
            <v-expand-transition>
              <div v-if="datosObtenidos">
                <v-divider class="my-4" />
                <v-alert type="success" text>
                  <strong>Datos obtenidos desde Registro Civil</strong>
                </v-alert>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle>RUT</v-list-item-subtitle>
                          <v-list-item-title class="text-h6">{{ rut }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle>Nombre Completo</v-list-item-subtitle>
                          <v-list-item-title class="text-h6">{{ nombreCompleto }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle>Fecha de Nacimiento</v-list-item-subtitle>
                          <v-list-item-title>{{ formatearFecha(datosRegistroCivil.fecha_nacimiento) }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle>Sexo</v-list-item-subtitle>
                          <v-list-item-title>{{ datosRegistroCivil.sexo === 'M' ? 'Masculino' : 'Femenino' }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle>Estado Vital</v-list-item-subtitle>
                          <v-list-item-title>
                            <v-chip
                              :color="datosRegistroCivil.fallecido ? 'error' : 'success'"
                              small
                              dark
                            >
                              {{ datosRegistroCivil.fallecido ? 'Fallecido' : 'Vivo' }}
                            </v-chip>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-subtitle>Edad</v-list-item-subtitle>
                          <v-list-item-title>
                            {{ calcularEdad(datosRegistroCivil.fecha_nacimiento, datosRegistroCivil.fecha_fallecimiento) }} años
                            <v-chip
                              v-if="datosRegistroCivil.es_mayor_75"
                              x-small
                              color="info"
                              class="ml-1"
                            >
                              Mayor de 75
                            </v-chip>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>

                <!-- Alerta de incompatibilidad edad/tipo certificado -->
                <v-alert v-if="tieneIncompatibilidadEdad" type="error" border="left" colored-border class="my-4">
                  <div class="font-weight-bold mb-2">❌ No puede crear postulación</div>
                  <div>{{ mensajeIncompatibilidadEdad }}</div>
                </v-alert>
                <v-alert v-if="estaFallecido" type="error" border="left" colored-border class="my-4">
                  <div class="font-weight-bold mb-2">Atención</div>
                  <div>Esta persona esta fallecida</div>
                </v-alert>

                <!-- Botón para crear postulación -->
                <v-divider class="my-4" />
                <div class="d-flex justify-end">
                  <v-btn
                    color="success"
                    large
                    :loading="creandoPostulacion"
                    :disabled="tieneIncompatibilidadEdad"
                    @click="crearPostulacion"
                  >
                    <v-icon left>mdi-plus-circle</v-icon>
                    Crear Postulación
                  </v-btn>
                </div>
              </div>
            </v-expand-transition>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { apiClient } from '@/admin/services/API'
import { notificar, notificarErrorResponse } from '@/admin/utils/notificar'

export default {
  name: 'IncentivosRetiroPostulacionBusqueda',
  data() {
    return {
      rut: '',
      buscandoRut: false,
      datosObtenidos: false,
      creandoPostulacion: false,
      certificadoInfo: null,
      datosRegistroCivil: {
        nombres: '',
        apellido_paterno: '',
        apellido_materno: '',
        fecha_nacimiento: '',
        fecha_fallecimiento: '',
        sexo: '',
        fallecido: false,
        es_mayor_75: false,
      },
      rules: {
        required: v => !!v || 'Campo requerido',
        rut: v => {
          if (!v) return true
          const rutRegex = /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/
          return rutRegex.test(v) || 'RUT inválido (formato: XX.XXX.XXX-X)'
        },
      },
    }
  },
  computed: {
    nombreCompleto() {
      const partes = [
        this.datosRegistroCivil.nombres,
        this.datosRegistroCivil.apellido_paterno,
        this.datosRegistroCivil.apellido_materno,
      ].filter(Boolean)
      return partes.join(' ')
    },
    tieneIncompatibilidadEdad() {
      if (!this.datosObtenidos || !this.certificadoInfo) return false
      
      const esMayor75 = this.datosRegistroCivil.es_mayor_75
      const esMayor65 = this.calcularEdad(this.datosRegistroCivil.fecha_nacimiento, this.datosRegistroCivil.fecha_fallecimiento) > 65
      const tipoCertificado = this.certificadoInfo.tipo
      
      // Mayor de 75 o menor de 66 no puede postular a proceso de menores de 75
      if ((esMayor75 || (!esMayor65 && !this.cumple66EsteAnio())) && tipoCertificado === 'ley_21724_menor_75') {
        return true
      }

      // Menor de 75 no puede postular a proceso de mayores de 75
      if (!esMayor75 && !this.cumple75EsteAnio() && tipoCertificado === 'ley_21724_mayor_75') {
        return true
      }

      if ((esMayor75 || this.cumple75EsteAnio()) && !this.esPostulacionValidaPorTramo()) {
        return true
      }
      
      return false
    },
    estaFallecido() {
      if (this.datosObtenidos && this.datosRegistroCivil.fallecido) {
        return true
      }

      return false
    },
    mensajeIncompatibilidadEdad() {
      if (!this.certificadoInfo) return ''
      
      const tipoCertificado = this.certificadoInfo.tipo
      const esMayor75 = this.datosRegistroCivil.es_mayor_75
      const esMayor65 = this.calcularEdad(this.datosRegistroCivil.fecha_nacimiento, this.datosRegistroCivil.fecha_fallecimiento) > 65
      
      if (!esMayor65 && tipoCertificado === 'ley_21724_menor_75') {
        return 'Esta persona es menor de 66 años y no puede postular al proceso de menores de 75 años.'
      }

      if (esMayor75 && tipoCertificado === 'ley_21724_menor_75') {
        return 'Esta persona es mayor de 75 años y no puede postular al proceso de menores de 75 años. Debe crear un certificado tipo "Ley 21.724 (Mayor 75 años)" que le otorga asignación inmediata de cupo.'
      }
      
      if (!esMayor75 && !this.cumple75EsteAnio() && tipoCertificado === 'ley_21724_mayor_75') {
        return 'Esta persona es menor de 75 años y no puede postular al proceso de mayores de 75 años. Debe crear un certificado tipo "Ley 21.135" o "Ley 21.724 (Menor 75 años)" según corresponda.'
      }

      if ((esMayor75 || this.cumple75EsteAnio()) && !this.esPostulacionValidaPorTramo()) {
        return `Esta persona no puede postular porque no corresponde al tramo ${this.certificadoInfo.tramo}.`
      }
      
      return ''
    },
  },
  mounted() {
    this.cargarCertificado()
  },
  methods: {
    async cargarCertificado() {
      try {
        const certificadoId = this.$route.params.id
        const { data } = await apiClient.get(`/tramites/incentivos-retiro/certificados/${certificadoId}`)
        this.certificadoInfo = data.data
      } catch (error) {
        console.error('Error al cargar certificado:', error)
      }
    },
    esPostulacionValidaPorTramo() {
      if (!this.certificadoInfo.tramo) return true

      if (this.datosRegistroCivil.fecha_nacimiento === "0000-00-00" || !this.datosRegistroCivil.fecha_nacimiento) return false

      const edadActual = this.calcularEdad(this.datosRegistroCivil.fecha_nacimiento, this.datosRegistroCivil.fecha_fallecimiento)
      const datosFecha = this.datosRegistroCivil.fecha_nacimiento.split('T')[0].split('-').map(Number)
      const month = datosFecha[1]
      const dia = datosFecha[2]
      const mesFechaNacimiento = month - 1

      if (+this.certificadoInfo.tramo === 1) {
        // 30 junio
        if (this.cumple75EsteAnio() && ((mesFechaNacimiento < 5) || (mesFechaNacimiento === 5 && dia <= 30))) {
          return true
        }
        const fechaActual = new Date()
        return edadActual >= 75 && ((fechaActual.getMonth() < 5 ) || (fechaActual.getMonth() === 5 && fechaActual.getDate() <= 30))
      }
      if (+this.certificadoInfo.tramo === 2) {
        // 31 diciembre
        if (this.cumple75EsteAnio() && mesFechaNacimiento > 5 && ((mesFechaNacimiento < 11) || (mesFechaNacimiento === 11 && dia <= 31))) {
          return true
        }
        const fechaActual = new Date()
        return edadActual >= 75 && fechaActual.getMonth() > 5 && ((fechaActual.getMonth() < 11) || (fechaActual.getMonth() === 11 && fechaActual.getDate() <= 31))
      }

      return false
    },
    async buscarRut() {
      if (!this.rut) {
        notificar({ message: 'Debe ingresar un RUT', severity: 'error' })
        return
      }
      if (this.rules.rut(this.rut) !== true) {
        notificar({ message: 'Formato de RUT inválido. Por favor, ingrese un RUT en formato XX.XXX.XXX-X', severity: 'error' })
        return
      }

      if (!this.esValidoRut(this.rut)) {
        notificar({ message: 'RUT inválido. Por favor, ingrese un RUT válido', severity: 'error' })
        return
      }

      try {
        this.buscandoRut = true

        // Llamada real al servicio de Registro Civil
        const { data } = await apiClient.get(`/tramitesgenerales/informacion-personal/${this.rut}`)

        const datosPersona = {
          nombres: data.nombres?.trim() || '',
          apellido_paterno: data.apellido_paterno?.trim() || '',
          apellido_materno: data.apellido_materno?.trim() || '',
          fecha_nacimiento: data.fecha_nacimiento || '',
          fecha_fallecimiento: data.fecha_fallecimiento || '',
          sexo: data.sexo || '',
          fallecido: data.fecha_fallecimiento !== "0000-00-00" && data.fecha_fallecimiento !== null && data.fecha_fallecimiento !== "",
        }

        const edad = this.calcularEdad(datosPersona.fecha_nacimiento, datosPersona.fecha_fallecimiento)

        this.datosRegistroCivil = {
          ...datosPersona,
          es_mayor_75: edad >= 75,
        }

        this.datosObtenidos = true
        notificar({ message: 'Datos obtenidos correctamente desde Registro Civil', severity: 'success' })
      } catch (error) {
        notificarErrorResponse(error, 'Error al consultar Registro Civil')
      } finally {
        this.buscandoRut = false
      }
    },
    resetearBusqueda() {
      this.rut = ''
      this.datosObtenidos = false
      this.datosRegistroCivil = {
        nombres: '',
        apellido_paterno: '',
        apellido_materno: '',
        fecha_nacimiento: '',
        sexo: '',
        fallecido: false,
        es_mayor_75: false,
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return '-'
      try {
        const [year, month, day] = fecha.split('-')
        return `${day}/${month}/${year}`
      } catch {
        return '-'
      }
    },
    formatearRut() {
      // Limpiar y formatear RUT automáticamente
      this.$nextTick(() => {
        let rut = this.rut.replace(/[^\dkK]/g, '')
        if (rut.length >= 2) {
          const dv = rut.slice(-1)
          const numero = rut.slice(0, -1)
          rut = numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '-' + dv.toUpperCase()
          this.rut = rut
        }
      })
    },
    calcularEdad(fechaNacimiento, fechaFallecimiento = null) {
      if (!fechaNacimiento || fechaNacimiento === "0000-00-00") return 0
      const [year, month, day] = fechaNacimiento.split('T')[0].split('-').map(Number)
      const hoy = new Date()
      const nacimiento = new Date(year, month - 1, day)

      if (fechaFallecimiento && fechaFallecimiento !== "0000-00-00") {
        const [fYear, fMonth, fDay] = fechaFallecimiento.split('T')[0].split('-').map(Number)
        const fallecimiento = new Date(fYear, fMonth - 1, fDay)
        if (fallecimiento < hoy) {
          hoy.setTime(fallecimiento.getTime())
        }
      }


      let edad = hoy.getFullYear() - nacimiento.getFullYear()
      const mes = hoy.getMonth() - nacimiento.getMonth()
      if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
        edad--
      }
      return edad
    },
    cumple66EsteAnio() {
      const edadActual = this.calcularEdad(this.datosRegistroCivil.fecha_nacimiento, this.datosRegistroCivil.fecha_fallecimiento)

      if (this.datosRegistroCivil.fecha_nacimiento === "0000-00-00" || !this.datosRegistroCivil.fecha_nacimiento) return false

      const datosFecha = this.datosRegistroCivil.fecha_nacimiento.split('T')[0].split('-').map(Number)
      const month = datosFecha[1]
      const dia = datosFecha[2]
      const mesFechaNacimiento = month - 1

      return edadActual === 65 && (new Date().getMonth() < mesFechaNacimiento || (new Date().getMonth() === mesFechaNacimiento && new Date().getDate() < dia))
    },
    cumple75EsteAnio() {
      if (this.datosRegistroCivil.fecha_nacimiento === "0000-00-00" || !this.datosRegistroCivil.fecha_nacimiento) return false

      const edadActual = this.calcularEdad(this.datosRegistroCivil.fecha_nacimiento, this.datosRegistroCivil.fecha_fallecimiento)
      const datosFecha = this.datosRegistroCivil.fecha_nacimiento.split('T')[0].split('-').map(Number)
      const month = datosFecha[1]
      const dia = datosFecha[2]
      const mesFechaNacimiento = month - 1

      return edadActual === 74 && (new Date().getMonth() < mesFechaNacimiento || (new Date().getMonth() === mesFechaNacimiento && new Date().getDate() < dia))
    },
    cumplio75EsteAnio() {
      if (this.datosRegistroCivil.fecha_nacimiento === "0000-00-00" || !this.datosRegistroCivil.fecha_nacimiento) return false

      const edadActual = this.calcularEdad(this.datosRegistroCivil.fecha_nacimiento, this.datosRegistroCivil.fecha_fallecimiento)
      const datosFecha = this.datosRegistroCivil.fecha_nacimiento.split('T')[0].split('-').map(Number)
      const month = datosFecha[1]
      const dia = datosFecha[2]
      const mesFechaNacimiento = month - 1

      return edadActual === 75 && (new Date().getMonth() > mesFechaNacimiento || (new Date().getMonth() === mesFechaNacimiento && new Date().getDate() >= dia))
    },
    esValidoRut(rutCompleto) {
      // Eliminar puntos y guiones
      const cleanRut = rutCompleto.replace(/^0+/, '').replace(/[^0-9kK]/g, '');
      if (cleanRut.length < 8) return false;

      // Separar cuerpo y dígito verificador
      const body = cleanRut.slice(0, -1);
      const dv = cleanRut.slice(-1).toUpperCase();

      // Calcular dígito verificador (Módulo 11)
      let suma = 0;
      let multiplo = 2;

      for (let i = body.length - 1; i >= 0; i--) {
          suma += parseInt(body.charAt(i)) * multiplo;
          multiplo = multiplo < 7 ? multiplo + 1 : 2;
      }

      const dvEsperado = 11 - (suma % 11);
      const dvCalculado = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();
      // console.log(`Rut: ${rutCompleto}, Body: ${body}, DV: ${dv}, DV Calculado: ${dvCalculado}`);

      return dvCalculado === dv;
    },
    async crearPostulacion() {
      if (this.rules.rut(this.rut) !== true) {
        notificar({ message: 'Formato de RUT inválido. Por favor, ingrese un RUT en formato XX.XXX.XXX-X', severity: 'error' })
        return
      }

      try {
        this.creandoPostulacion = true
        
        const certificadoId = this.$route.params.id
        
        // Crear postulación con datos básicos del Registro Civil
        const payload = {
          rut: this.rut,
          nombres: this.datosRegistroCivil.nombres,
          apellido_paterno: this.datosRegistroCivil.apellido_paterno,
          apellido_materno: this.datosRegistroCivil.apellido_materno,
          fecha_nacimiento: this.datosRegistroCivil.fecha_nacimiento,
          fecha_fallecimiento: this.datosRegistroCivil.fecha_fallecimiento,
          sexo: this.datosRegistroCivil.sexo,
          fallecido: this.datosRegistroCivil.fallecido,
          es_mayor_75: this.datosRegistroCivil.es_mayor_75,
        }

        const { data } = await apiClient.post(
          `/tramites/incentivos-retiro/certificados/${certificadoId}/postulaciones`,
          payload
        )

        notificar({ message: 'Postulación creada correctamente', severity: 'success' })
        
        // Redirigir al formulario completo con el ID de la postulación
        const postulacionId = data.data?.id || data.id
        
        this.$router.push({
          name: 'incentivos-retiro-postulacion-editar',
          params: { 
            id: certificadoId,
            postulacionId: postulacionId
          },
        })
      } catch (error) {
        notificarErrorResponse(error, 'Error al crear la postulación')
      } finally {
        this.creandoPostulacion = false
      }
    },
    volver() {
      this.$router.push({
        name: 'incentivos-retiro-certificado-detalle',
        params: { id: this.$route.params.id },
      })
    },
    getTipoLeyColor(tipo) {
      const colores = {
        ley_21135: 'info',
        ley_21724_menor_75: 'info',
        ley_21724_mayor_75: 'info',
      }
      return colores[tipo] || 'grey'
    },
    getTipoLeyTexto(tipo) {
      const textos = {
        ley_21135: 'Ley 21.135',
        ley_21724_menor_75: 'Ley 21.724 (Menor 75 años)',
        ley_21724_mayor_75: 'Ley 21.724 (Mayor 75 años)',
      }
      return textos[tipo] || tipo
    },
    getTipoLeyDescripcion(tipo) {
      const descripciones = {
        ley_21135: 'Proceso Regular - Hombres 65 años, Mujeres 60 años',
        ley_21724_menor_75: 'Artículo 88 - Postulantes de 66 años (beneficios decrecientes)',
        ley_21724_mayor_75: 'Artículo 88 - Postulantes 75+ años (asignación inmediata de cupo)',
      }
      return descripciones[tipo] || ''
    },
  },
}
</script>

<style scoped>
.v-list-item__title.text-h6 {
  font-weight: 600;
}
</style>
